import React, { useState, useEffect, useMemo } from 'react';
import { StaffService, PayrollService } from '../services/api';
import Modal from './common/Modal';
import Swal from 'sweetalert2';
import { useSettings } from '../context/SettingsContext';

import { exportToExcel } from '../utils/exportHelper';

const StaffList = () => {
  const { settings, getCurrencySymbol } = useSettings();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [viewingStaff, setViewingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('directory'); // 'directory' or 'payroll'
  const [payrolls, setPayrolls] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [payrollFormData, setPayrollFormData] = useState({
    base_salary: '',
    bonus: 0,
    deductions: 0,
    payment_date: new Date().toISOString().split('T')[0],
    note: ''
  });
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [user] = useState(JSON.parse(localStorage.getItem('user')));
  const userRole = user?.role || user?.data?.role;
  const canExport = userRole === 'admin' || userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    designation: '',
    salary: '',
    joining_date: '',
    gender: '',
    qualification: '',
    address: '',
    photo: null
  });

  // Stats
  const [stats, setStats] = useState({ total: 0, teachers: 0, admin: 0, monthly_payroll: 0, total_paid: 0 });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAllData();
  }, [activeTab]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [staffRes, payrollRes] = await Promise.all([
        StaffService.getAll(),
        PayrollService.getAll()
      ]);

      setStaff(staffRes.data);
      setPayrolls(payrollRes.data);
      calculateStats(staffRes.data, payrollRes.data);
    } catch (error) {
      console.error("Error loading data", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayroll = async () => {
    try {
      setPayrollLoading(true);
      const response = await PayrollService.getAll();
      setPayrolls(response.data);
    } catch (error) {
      console.error("Error loading payroll", error);
    } finally {
      setPayrollLoading(false);
    }
  };

  const loadStaff = async () => {
    try {
      const response = await StaffService.getAll();
      const data = response.data;
      setStaff(data);
      calculateStats(data);
    } catch (error) {
      console.error("Error loading staff", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (staffData, payrollData) => {
    const total = staffData.length;
    const teachers = staffData.filter(s => s.designation === 'Teacher').length;
    const admin = staffData.filter(s => ['Admin', 'Accountant', 'Receptionist', 'Principal', 'Coordinator'].includes(s.designation)).length;
    const monthly_payroll = staffData.reduce((acc, curr) => acc + (parseFloat(curr.salary) || 0), 0);

    // Calculate total paid this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const total_paid = payrollData
      .filter(p => {
        const d = new Date(p.payment_date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      })
      .reduce((acc, curr) => acc + (parseFloat(curr.net_salary) || 0), 0);

    setStats({ total, teachers, admin, monthly_payroll, total_paid });
  };

  const handleInputChange = (e) => {
    if (e.target.name === 'photo') {
      const file = e.target.files[0];
      setFormData({ ...formData, photo: file });
      if (file) {
        setPhotoPreview(URL.createObjectURL(file));
      } else {
        setPhotoPreview(null);
      }
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const togglePermission = (perm) => {
    const currentPerms = formData.permissions || [];
    const newPerms = currentPerms.includes(perm)
      ? currentPerms.filter(p => p !== perm)
      : [...currentPerms, perm];
    setFormData({ ...formData, permissions: newPerms });
  };

  const handleAddNew = () => {
    setEditingStaff(null);
    setPhotoPreview(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      designation: '',
      salary: '',
      joining_date: '',
      gender: '',
      qualification: '',
      address: '',
      permissions: [],
      photo: null
    });
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setEditingStaff(member);
    setPhotoPreview(member.photo ? `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${member.photo}` : null);
    setFormData({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      designation: member.designation || '',
      salary: member.salary || '',
      joining_date: member.joining_date || '',
      gender: member.gender || '',
      qualification: member.qualification || '',
      address: member.address || '',
      permissions: member.permissions || [],
      photo: member.photo
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4361ee',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await StaffService.delete(id);
        Swal.fire('Deleted!', 'Staff member has been deleted.', 'success');
        loadAllData();
      } catch (error) {
        console.error("Error deleting staff", error);
        Swal.fire('Error!', 'Failed to delete staff member.', 'error');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);

    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== null) {
        if (key === 'permissions') {
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      }
    });

    try {
      if (editingStaff) {
        await StaffService.update(editingStaff.id, submitData);
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Staff details updated successfully.',
          timer: 1500
        });
      } else {
        await StaffService.create(submitData);
        Swal.fire({
          icon: 'success',
          title: 'Saved!',
          text: 'New staff member added successfully.',
          timer: 1500
        });
      }
      setShowModal(false);
      loadAllData();
    } catch (error) {
      console.error("Error saving staff", error);
      if (!error.isLimitError) {
        let errorMessage = 'Failed to save staff member. Please check inputs.';
        if (error.response?.data) {
          if (error.response.data.errors) {
            errorMessage = Object.values(error.response.data.errors).flat().join('\n');
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: errorMessage
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const filteredStaff = useMemo(() => {
    return staff.filter(member =>
      member.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.designation?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [staff, searchTerm]);

  const filteredPayrolls = useMemo(() => {
    return payrolls.filter(pay => {
      const d = new Date(pay.payment_date);
      const matchesMonth = d.getMonth() + 1 === parseInt(payrollMonth);
      const matchesYear = d.getFullYear() === parseInt(payrollYear);
      const memberName = `${pay.staff?.first_name} ${pay.staff?.last_name}`.toLowerCase();
      const matchesSearch = memberName.includes(searchTerm.toLowerCase());
      return matchesMonth && matchesYear && matchesSearch;
    });
  }, [payrolls, payrollMonth, payrollYear, searchTerm]);

  const handlePaySalary = (member) => {
    setSelectedStaff(member);
    const today = new Date().toISOString().split('T')[0];
    const dToday = new Date();

    const existing = payrolls.find(p => {
      const d = new Date(p.payment_date);
      return p.staff_id === member.id && d.getMonth() === dToday.getMonth() && d.getFullYear() === dToday.getFullYear();
    });

    if (existing) {
      setEditingPayroll(existing);
      setPayrollFormData({
        base_salary: existing.base_salary,
        bonus: existing.bonus,
        deductions: existing.deductions,
        payment_date: existing.payment_date,
        note: existing.note || ''
      });
    } else {
      setEditingPayroll(null);
      setPayrollFormData({
        base_salary: member.salary,
        bonus: 0,
        deductions: 0,
        payment_date: today,
        note: ''
      });
    }
    setShowPayrollModal(true);
  };

  const handlePayrollDateChange = (newDate) => {
    const selectedDate = new Date(newDate);
    const month = selectedDate.getMonth();
    const year = selectedDate.getFullYear();

    const existing = payrolls.find(p => {
      const d = new Date(p.payment_date);
      return p.staff_id === selectedStaff.id && d.getMonth() === month && d.getFullYear() === year;
    });

    if (existing) {
      setEditingPayroll(existing);
      setPayrollFormData({
        base_salary: existing.base_salary,
        bonus: existing.bonus,
        deductions: existing.deductions,
        payment_date: newDate,
        note: existing.note || ''
      });
    } else {
      setEditingPayroll(null);
      setPayrollFormData({
        base_salary: selectedStaff.salary,
        bonus: 0,
        deductions: 0,
        payment_date: newDate,
        note: ''
      });
    }
  };

  const submitPayroll = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        staff_id: selectedStaff.id,
        ...payrollFormData
      };

      if (editingPayroll) {
        await PayrollService.update(editingPayroll.id, payload);
        Swal.fire('Updated', 'Salary record updated successfully!', 'success');
      } else {
        await PayrollService.create(payload);
        Swal.fire('Success', 'Salary paid successfully!', 'success');
      }

      setShowPayrollModal(false);
      loadAllData();
    } catch (error) {
      console.error("Error processing payroll", error);
      Swal.fire('Error', 'Failed to process payment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (activeTab === 'directory') {
      if (filteredStaff.length === 0) {
        Swal.fire('No Data', 'No staff records to export.', 'info');
        return;
      }
      const exportData = filteredStaff.map(member => ({
        'Full Name': `${member.first_name} ${member.last_name}`,
        'Email': member.email,
        'Phone': member.phone,
        'Designation': member.designation,
        'Salary': member.salary,
        'Joining Date': member.joining_date,
        'Gender': member.gender || '-',
        'Qualification': member.qualification || '-',
        'Address': member.address || '-'
      }));
      exportToExcel(exportData, `Staff_Directory_Export_${new Date().toISOString().split('T')[0]}`);
    } else {
      if (filteredPayrolls.length === 0) {
        Swal.fire('No Data', 'No payroll records to export for this period.', 'info');
        return;
      }
      const exportData = filteredPayrolls.map(pay => ({
        'Staff Name': `${pay.staff?.first_name} ${pay.staff?.last_name}`,
        'Base Salary': pay.base_salary,
        'Bonus': pay.bonus,
        'Deductions': pay.deductions,
        'Net Salary': pay.net_salary,
        'Payment Date': pay.payment_date,
        'Note': pay.note || '-'
      }));
      const monthLabel = new Date(0, parseInt(payrollMonth) - 1).toLocaleString('default', { month: 'long' });
      exportToExcel(exportData, `Payroll_Export_${monthLabel}_${payrollYear}`);
    }
  };


  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="fw-bold">Staff & HR Management</h2>
          <div className="d-flex gap-3 mt-2">
            <button
              className={`btn btn-sm rounded-pill px-4 ${activeTab === 'directory' ? 'btn-primary shadow-sm' : 'btn-light text-muted'}`}
              onClick={() => setActiveTab('directory')}
            >
              Employee Directory
            </button>
            <button
              className={`btn btn-sm rounded-pill px-4 ${activeTab === 'payroll' ? 'btn-primary shadow-sm' : 'btn-light text-muted'}`}
              onClick={() => setActiveTab('payroll')}
            >
              Payroll History
            </button>
          </div>
        </div>
        <div className="d-flex gap-2">
          {canExport && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleAddNew}>
            <i className="bi bi-plus-lg"></i> Add Staff
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid mb-5">
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Total Staff</p>
            <h3 className="mb-0">{stats.total}</h3>
          </div>
          <div className="stat-icon bg-blue-light">
            <i className="bi bi-people-fill"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Teachers</p>
            <h3 className="mb-0">{stats.teachers}</h3>
          </div>
          <div className="stat-icon bg-orange-light">
            <i className="bi bi-person-video3"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1  small fw-bold">Admin & Support</p>
            <h3 className="mb-0">{stats.admin}</h3>
          </div>
          <div className="stat-icon bg-dark-light">
            <i className="bi bi-building"></i>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <p className="text-muted mb-1 small fw-bold">Est. Payroll</p>
            <h3 className="mb-0">{getCurrencySymbol(settings?.currency)}{stats.monthly_payroll.toLocaleString()}</h3>
            <div className="text-success small fw-bold mt-1">
              Paid: {getCurrencySymbol(settings?.currency)}{stats.total_paid.toLocaleString()}
            </div>
          </div>
          <div className="stat-icon bg-red-light">
            <i className="bi bi-cash-stack"></i>
          </div>
        </div>
      </div>

      <div className="card-box shadow-sm border">
        <div className="section-header px-4 d-flex justify-content-between align-items-center mb-3 flex-wrap-mobile">
          <h5 className="mb-0 fw-bold">{activeTab === 'directory' ? 'Employee Directory' : 'Payroll Records'}</h5>
          <div className="d-flex align-items-center gap-3 flex-wrap-mobile">
            {activeTab === 'payroll' && (
              <>
                <select
                  className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
                  style={{ height: '40px' }}
                  value={payrollMonth}
                  onChange={(e) => setPayrollMonth(e.target.value)}
                >
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm rounded-pill border-0 shadow-sm bg-light px-3"
                  style={{ height: '40px' }}
                  value={payrollYear}
                  onChange={(e) => setPayrollYear(e.target.value)}
                >
                  {[2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </>
            )}
            <div className="position-relative w-100">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)' }}></i>
              <input
                type="text"
                className="form-control rounded-pill ps-5 bg-light border-0"
                placeholder="Search name, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ height: '40px' }}
              />
            </div>
          </div>
        </div>

        {activeTab === 'directory' ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">Employee Name</th>
                  <th>Designation</th>
                  <th>Contact</th>
                  <th>Joining Date</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">Loading staff details...</td></tr>
                ) : filteredStaff.length > 0 ? (
                  filteredStaff.map((member) => (
                    <tr key={member.id}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="avatar-sm text-white d-flex align-items-center justify-content-center rounded-circle fw-bold shadow-sm overflow-hidden"
                            style={{ width: 42, height: 42, fontSize: '1.2rem', background: '#2b2e34' }}
                          >
                            {member.photo ? (
                              <img src={member.photo.startsWith('http') ? member.photo : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${member.photo}`} alt={`${member.first_name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              member.first_name.charAt(0)
                            )}
                          </div>
                          <div>
                            <div className="fw-semibold text-dark">{member.first_name} {member.last_name}</div>
                            <small className="text-muted">{member.email}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge rounded-pill fw-normal px-3 py-2 ${member.designation === 'Teacher' ? 'bg-red-light text-primary' : 'bg-light text-dark border'}`}>
                          {member.designation}
                        </span>
                      </td>
                      <td>{member.phone}</td>
                      <td>{new Date(member.joining_date).toLocaleDateString()}</td>
                      <td className="text-end pe-4">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            className="btn btn-icon btn-light rounded-circle text-info shadow-sm d-flex align-items-center justify-content-center p-0"
                            style={{ width: 35, height: 35 }}
                            title="View Profile"
                            onClick={() => setViewingStaff(member)}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                className="btn btn-icon btn-light rounded-circle text-primary shadow-sm d-flex align-items-center justify-content-center p-0"
                                style={{ width: 35, height: 35 }}
                                title="Edit Profile"
                                onClick={() => handleEdit(member)}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </button>
                              <button
                                className="btn btn-icon btn-light rounded-circle text-danger shadow-sm d-flex align-items-center justify-content-center p-0"
                                style={{ width: 35, height: 35 }}
                                title="Delete Staff"
                                onClick={() => handleDelete(member.id)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="5" className="text-center py-5 text-muted">No staff members found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="ps-4">Staff Member</th>
                  <th>Payment Date</th>
                  <th className="text-success">Net Salary</th>
                  <th>Bonus / Ded.</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {payrollLoading ? (
                  <tr><td colSpan="5" className="text-center py-5">Loading payroll history...</td></tr>
                ) : filteredPayrolls.length > 0 ? (
                  filteredPayrolls.map(pay => (
                    <tr key={pay.id}>
                      <td className="ps-4 fw-bold">{pay.staff?.first_name} {pay.staff?.last_name}</td>
                      <td>{new Date(pay.payment_date).toLocaleDateString()}</td>
                      <td className="fw-bold text-success">{getCurrencySymbol(settings?.currency)}{pay.net_salary.toLocaleString()}</td>
                      <td><span className="text-primary">+{pay.bonus}</span> / <span className="text-danger">-{pay.deductions}</span></td>
                      <td className="text-center"><span className="badge bg-success-subtle text-success rounded-pill px-3">Paid</span></td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="5" className="text-center py-5">No payment history found for this period.</td></tr>
                )}
              </tbody>
            </table>
            {filteredPayrolls.length > 0 && (
              <div className="p-3 bg-light border-top text-end pe-4">
                <span className="text-muted me-2">Total Paid this Month:</span>
                <span className="h5 fw-bold text-success mb-0">
                  {getCurrencySymbol(settings?.currency)}
                  {filteredPayrolls.reduce((acc, curr) => acc + parseFloat(curr.net_salary), 0).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal show={showModal} onClose={() => setShowModal(false)} title={editingStaff ? "Edit Staff Member" : "Add New Staff Member"} size="modal-lg">
        <form onSubmit={handleSubmit}>
          <div className="mb-4 text-center">
            <label className="form-label small text-muted fw-bold d-block mb-3">Staff Photo</label>
            <div className="d-flex justify-content-center">
              <div className="position-relative" style={{ width: 120, height: 120 }}>
                <div
                  className="rounded-circle border border-3 border-primary shadow-sm overflow-hidden bg-light d-flex align-items-center justify-content-center"
                  style={{ width: '100%', height: '100%' }}
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <i className="bi bi-person-circle text-muted" style={{ fontSize: '4rem' }}></i>
                  )}
                </div>

                <label
                  htmlFor="photoInput"
                  className="position-absolute bg-primary text-white rounded-circle d-flex align-items-center justify-content-center shadow"
                  style={{
                    width: 36,
                    height: 36,
                    bottom: 0,
                    right: 0,
                    cursor: 'pointer',
                    border: '3px solid white'
                  }}
                  title="Upload Photo"
                >
                  <i className="bi bi-camera-fill"></i>
                </label>

                {photoPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, photo: null });
                      setPhotoPreview(null);
                      const input = document.getElementById('photoInput');
                      if (input) input.value = '';
                    }}
                    className="position-absolute bg-danger text-white rounded-circle d-flex align-items-center justify-content-center shadow border-0"
                    style={{
                      width: 36,
                      height: 36,
                      top: 0,
                      right: 0,
                      border: '3px solid white'
                    }}
                    title="Remove Photo"
                  >
                    <i className="bi bi-trash-fill"></i>
                  </button>
                )}

                <input
                  id="photoInput"
                  name="photo"
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <small className="text-muted d-block mt-2">JPG, PNG, GIF (Max 2MB)</small>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">First Name</label>
              <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleInputChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Last Name</label>
              <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleInputChange} required />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Email</label>
            <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} required />
          </div>
          <div className="mb-3">
            <label className="form-label  small text-muted fw-bold">Phone</label>
            <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} required />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Designation</label>
              <select className="form-select" name="designation" value={formData.designation} onChange={handleInputChange} required>
                <option value="">Select Designation</option>
                <option value="Principal">Principal</option>
                <option value="Vice Principal">Vice Principal</option>
                <option value="Coordinator">Coordinator</option>
                <option value="Teacher">Teacher</option>
                <option value="Accountant">Accountant</option>
                <option value="Receptionist">Receptionist</option>
                <option value="Librarian">Librarian</option>
                <option value="IT Support">IT Support</option>
                <option value="Lab Assistant">Lab Assistant</option>
                <option value="Security Guard">Security Guard</option>
                <option value="Office Boy">Office Boy</option>
                <option value="Driver">Driver</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Salary ({getCurrencySymbol(settings?.currency)})</label>
              <input type="number" className="form-control" name="salary" value={formData.salary} onChange={handleInputChange} required />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Gender</label>
              <select className="form-select" name="gender" value={formData.gender} onChange={handleInputChange}>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label  small text-muted fw-bold">Joining Date</label>
              <input type="date" className="form-control" name="joining_date" value={formData.joining_date} onChange={handleInputChange} required />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Highest Qualification</label>
              <input type="text" className="form-control" name="qualification" value={formData.qualification} onChange={handleInputChange} placeholder="e.g. Master in Education" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Address</label>
              <textarea className="form-control" name="address" rows="1" value={formData.address} onChange={handleInputChange} placeholder="Home Address"></textarea>
            </div>
          </div>

          <div className="mb-4">
            <label className="form-label small text-muted fw-bold mb-3 d-block">Module Permissions</label>
            <div className="card border-0 bg-light-purple-subtle p-3 rounded-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_staff_attendance" checked={formData.permissions?.includes('manage_staff_attendance')} onChange={() => togglePermission('manage_staff_attendance')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_staff_attendance">
                      Manage Staff Attendance
                      <small className="d-block text-muted fw-normal">Allow marking attendance for other employees.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_manage_fees" checked={formData.permissions?.includes('manage_fees')} onChange={() => togglePermission('manage_fees')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_manage_fees">
                      Manage Fees
                      <small className="d-block text-muted fw-normal">Allow collecting and viewing fee records.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_add_student" checked={formData.permissions?.includes('add_student')} onChange={() => togglePermission('add_student')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_add_student">
                      Add Student
                      <small className="d-block text-muted fw-normal">Allow manual entry of new student records.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_import_student" checked={formData.permissions?.includes('import_student')} onChange={() => togglePermission('import_student')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_import_student">
                      Import Student
                      <small className="d-block text-muted fw-normal">Allow bulk importing students from data files.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_manage_expenses" checked={formData.permissions?.includes('manage_expenses')} onChange={() => togglePermission('manage_expenses')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_manage_expenses">
                      Manage Expenses
                      <small className="d-block text-muted fw-normal">Allow recording and viewing school expenses.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_manage_courses" checked={formData.permissions?.includes('manage_courses')} onChange={() => togglePermission('manage_courses')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_manage_courses">
                      Manage Courses
                      <small className="d-block text-muted fw-normal">Allow creating and editing courses/classes.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_manage_exams" checked={formData.permissions?.includes('manage_exams')} onChange={() => togglePermission('manage_exams')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_manage_exams">
                      Manage Exams
                      <small className="d-block text-muted fw-normal">Allow scheduling exams and deleting records.</small>
                    </label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input custom-switch" type="checkbox" id="perm_manage_inventory" checked={formData.permissions?.includes('manage_inventory')} onChange={() => togglePermission('manage_inventory')} />
                    <label className="form-check-label ps-2 fw-semibold text-dark" htmlFor="perm_manage_inventory">
                      Manage Inventory
                      <small className="d-block text-muted fw-normal">Allow managing school assets and items.</small>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : (editingStaff ? 'Update Staff' : 'Save Staff')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal show={!!viewingStaff} onClose={() => setViewingStaff(null)} title="Staff Profile Details">
        {viewingStaff && (
          <div className="student-details">
            <div className="text-center mb-4">
              {viewingStaff.photo ? (
                <img
                  src={viewingStaff.photo.startsWith('http') ? viewingStaff.photo : `${process.env.NODE_ENV === "development" ? "http://localhost:8000" : "https://lms.appy.pk"}${viewingStaff.photo}`}
                  alt="Profile"
                  className="rounded-circle shadow border"
                  style={{ width: 100, height: 100, objectFit: 'cover' }}
                />
              ) : (
                <div
                  className="avatar-lg text-white d-inline-flex align-items-center justify-content-center rounded-circle fw-bold shadow"
                  style={{ width: 80, height: 80, fontSize: '2rem', background: '#2b2e34' }}
                >
                  {viewingStaff.first_name.charAt(0)}
                </div>
              )}
              <h4 className="mt-3 mb-1">{viewingStaff.first_name} {viewingStaff.last_name}</h4>
              <span className="badge bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3 py-1 mb-2">
                {viewingStaff.designation}
              </span>
              <div className="text-muted small mt-1">
                <i className="bi bi-envelope me-1"></i>{viewingStaff.email}
                <span className="mx-2">•</span>
                <i className="bi bi-telephone me-1"></i>{viewingStaff.phone || 'N/A'}
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block fw-bold mb-1">Gender</small>
                  <div className="fw-medium">{viewingStaff.gender || 'Not Specified'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block fw-bold mb-1">Joining Date</small>
                  <div className="fw-medium">{new Date(viewingStaff.joining_date).toLocaleDateString()}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block fw-bold mb-1">Highest Qualification</small>
                  <div className="fw-medium text-truncate">{viewingStaff.qualification || 'Not Specified'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block fw-bold mb-1">Current Salary</small>
                  <div className="fw-medium text-success">{getCurrencySymbol(settings?.currency)}{parseFloat(viewingStaff.salary).toLocaleString()}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="p-3 bg-light rounded border">
                  <small className="text-muted d-block fw-bold mb-1">Address</small>
                  <div className="fw-medium">{viewingStaff.address || 'Not Specified'}</div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setViewingStaff(null)}>Close</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        show={showPayrollModal}
        onClose={() => setShowPayrollModal(false)}
        title={editingPayroll ? `Edit Payment - ${selectedStaff?.first_name} ${selectedStaff?.last_name}` : `Pay Salary - ${selectedStaff?.first_name} ${selectedStaff?.last_name}`}
      >
        <form onSubmit={submitPayroll}>
          <div className="row g-3">
            <div className="col-12">
              <div className="d-flex justify-content-between">
                <label className="form-label small fw-bold text-muted">Base Salary</label>
                <span className="text-secondary small">Profile Basic: {getCurrencySymbol(settings?.currency)}{parseFloat(selectedStaff?.salary || 0).toLocaleString()}</span>
              </div>
              <input
                type="number"
                className="form-control"
                value={payrollFormData.base_salary}
                onChange={(e) => setPayrollFormData({ ...payrollFormData, base_salary: e.target.value })}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">Bonus</label>
              <input
                type="number"
                className="form-control"
                value={payrollFormData.bonus}
                onChange={(e) => setPayrollFormData({ ...payrollFormData, bonus: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">Deductions</label>
              <input
                type="number"
                className="form-control"
                value={payrollFormData.deductions}
                onChange={(e) => setPayrollFormData({ ...payrollFormData, deductions: e.target.value })}
              />
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold text-muted">Payment Date</label>
              <input
                type="date"
                className="form-control"
                value={payrollFormData.payment_date}
                onChange={(e) => handlePayrollDateChange(e.target.value)}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label small fw-bold text-muted">Notes</label>
              <textarea
                className="form-control"
                rows="2"
                value={payrollFormData.note}
                onChange={(e) => setPayrollFormData({ ...payrollFormData, note: e.target.value })}
              ></textarea>
            </div>
            <div className="col-12 mt-4 text-center">
              <div className="p-3 bg-light rounded-pill border">
                <span className="text-muted me-2">Net Total:</span>
                <span className="h4 fw-bold text-success mb-0">
                  {getCurrencySymbol(settings?.currency)}
                  {(parseFloat(payrollFormData.base_salary || 0) + parseFloat(payrollFormData.bonus || 0) - parseFloat(payrollFormData.deductions || 0)).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowPayrollModal(false)}>Cancel</button>
            <button type="submit" className={`btn ${editingPayroll ? 'btn-success' : 'btn-primary'} px-4 rounded-pill d-flex align-items-center gap-2`} disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Processing...' : (editingPayroll ? 'Save Changes' : 'Process Payment')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StaffList;
